import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../css/NewsPage.css';

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const PAGE_SIZE = 6;

const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [subcategory, setSubcategory] = useState('All');
  const [page, setPage] = useState(1);
  const [shortLinks, setShortLinks] = useState({});
  const [shortening, setShortening] = useState({});
  const [copiedSlug, setCopiedSlug] = useState('');
  const [importantSlug, setImportantSlug] = useState('');
  const [savedSlug, setSavedSlug] = useState('');
  const [shareStatus, setShareStatus] = useState({});
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submitUrl, setSubmitUrl] = useState('');
  const [submitTitle, setSubmitTitle] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');
  const [resolvedUsername, setResolvedUsername] = useState('');
  const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://urlshortener-serverside.onrender.com/api';

  const resolveUsername = useCallback(async () => {
    const cached = localStorage.getItem('username');
    if (cached) {
      setResolvedUsername(cached);
      return cached;
    }
    const token = localStorage.getItem('token');
    if (!token) return '';
    try {
      const response = await axios.get(`${BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.username) {
        localStorage.setItem('username', response.data.username);
        setResolvedUsername(response.data.username);
        return response.data.username;
      }
    } catch (error) {
      // ignore
    }
    return '';
  }, [BASE_URL]);

  const fetchNews = useCallback(async () => {
    try {
      const response = await axios.get(`${BASE_URL}/news`);
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.posts;
      setPosts(Array.isArray(list) ? list : []);
      setLastUpdatedAt(data?.lastUpdatedAt || '');
      setError('');
    } catch (err) {
      setError('Could not load news right now.');
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    let isMounted = true;
    const guardedFetch = async () => {
      if (!isMounted) return;
      await resolveUsername();
      await fetchNews();
    };

    guardedFetch();
    const interval = setInterval(guardedFetch, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchNews]);

  useEffect(() => {
    const title = 'BlinkURL Newsroom - Live updates';
    document.title = title;
    const description = 'Live updates across world, business, technology, football, and finance headlines.';
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', `${window.location.origin}/news`);
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
  }, []);

  const categories = useMemo(() => {
    const values = new Set(posts.map((post) => post.category || 'News'));
    return ['All', ...Array.from(values)];
  }, [posts]);

  const subcategories = useMemo(() => {
    const base = posts.filter((post) => category === 'All' || post.category === category);
    const values = new Set(base.map((post) => post.subcategory || 'General'));
    return ['All', ...Array.from(values)];
  }, [posts, category]);

  const quickCategories = useMemo(
    () => categories.filter((value) => value !== 'All').slice(0, 6),
    [categories]
  );

  const summary = useMemo(() => {
    const sources = new Set();
    let latestTime = 0;
    const categorySet = new Set();
    posts.forEach((post) => {
      if (post.category) categorySet.add(post.category);
      if (post.url) {
        try {
          sources.add(new URL(post.url).hostname.replace(/^www\./, ''));
        } catch (error) {
          // ignore
        }
      }
      if (post.publishedAt) {
        const time = new Date(post.publishedAt).getTime();
        if (!Number.isNaN(time)) latestTime = Math.max(latestTime, time);
      }
    });
    return {
      totalPosts: posts.length,
      categories: categorySet.size || 0,
      sources: sources.size || 0,
      latestStory: latestTime ? new Date(latestTime).toLocaleString() : '-',
    };
  }, [posts]);

  const filtered = useMemo(() => {
    return posts.filter((post) => {
      const matchesCategory = category === 'All' || (post.category || 'News') === category;
      const matchesSubcategory = subcategory === 'All' || (post.subcategory || 'General') === subcategory;
      const lower = query.trim().toLowerCase();
      const matchesQuery =
        !lower ||
        post.title?.toLowerCase().includes(lower) ||
        post.excerpt?.toLowerCase().includes(lower);
      return matchesCategory && matchesSubcategory && matchesQuery;
    });
  }, [posts, category, subcategory, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const featured = filtered[0];

  const refreshNews = async () => {
    setIsRefreshing(true);
    await fetchNews();
    setIsRefreshing(false);
  };

  const buildShortLink = async (post) => {
    if (!post?.url) return '';
    if (shortLinks[post.slug]) return shortLinks[post.slug];
    setShortening((prev) => ({ ...prev, [post.slug]: true }));
    try {
      const response = await axios.post(
        `${BASE_URL}/links/shorten`,
        {
          originalUrl: post.url,
          creator: localStorage.getItem('username') || 'news',
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const shortCode = response.data?.shortUrl;
      const full = shortCode ? `${window.location.origin}/${shortCode}` : '';
      setShortLinks((prev) => ({ ...prev, [post.slug]: full }));
      return full;
    } catch (err) {
      return '';
    } finally {
      setShortening((prev) => ({ ...prev, [post.slug]: false }));
    }
  };

  const readFullSource = async (post) => {
    const full = await buildShortLink(post);
    window.open(full || post.url, '_blank', 'noopener,noreferrer');
  };

  const copyShortSource = async (post) => {
    const full = shortLinks[post.slug] || (await buildShortLink(post));
    if (!full) return;
    await navigator.clipboard.writeText(full);
    setCopiedSlug(post.slug);
    setTimeout(() => setCopiedSlug(''), 1500);
  };

  const markImportant = async (post) => {
    const username = resolvedUsername || (await resolveUsername()) || 'news';
    const token = localStorage.getItem('token');
    try {
      setShareStatus((prev) => ({ ...prev, [post.slug]: 'Sharing...' }));
      await axios.post(
        `${BASE_URL}/social/important?username=${encodeURIComponent(username)}`,
        {
          username,
          slug: post.slug,
          title: post.title,
          url: post.url,
        }
      );
      const resp = await axios.post(
        `${BASE_URL}/social/share-now?username=${encodeURIComponent(username)}`,
        { username, post },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-user': username,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      const results = resp?.data?.results || [];
      const anyOk = results.some((r) => r.ok);
      setShareStatus((prev) => ({
        ...prev,
        [post.slug]: anyOk ? 'Shared!' : `Failed: ${results[0]?.details?.detail || results[0]?.details?.message || 'Check connections'}`,
      }));
      setImportantSlug(post.slug);
      setTimeout(() => setImportantSlug(''), 1500);
    } catch (error) {
      setShareStatus((prev) => ({
        ...prev,
        [post.slug]: 'Share failed. Check connections.',
      }));
    }
  };

  const savePost = async (post) => {
    const username = resolvedUsername || (await resolveUsername()) || 'news';
    const token = localStorage.getItem('token');
    await axios.post(
      `${BASE_URL}/news/save?username=${encodeURIComponent(username)}`,
      {
        username,
        url: post.url,
        title: post.title,
        image: post.image,
        category: post.category,
        subcategory: post.subcategory,
      },
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    setSavedSlug(post.slug);
    setTimeout(() => setSavedSlug(''), 1500);
  };

  const submitStory = async (e) => {
    e.preventDefault();
    if (!submitUrl) return;
    const username = resolvedUsername || (await resolveUsername()) || 'user';
    const token = localStorage.getItem('token');
    setSubmitStatus('Submitting...');
    try {
      await axios.post(
        `${BASE_URL}/news/submit`,
        {
          url: submitUrl,
          title: submitTitle,
          username,
          category: 'Football',
          subcategory: 'Football News',
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      setSubmitStatus('Submitted!');
      setSubmitUrl('');
      setSubmitTitle('');
      const response = await axios.get(`${BASE_URL}/news`);
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.posts;
      setPosts(Array.isArray(list) ? list : []);
      setLastUpdatedAt(data?.lastUpdatedAt || '');
    } catch (err) {
      setSubmitStatus('Submission failed.');
    } finally {
      setTimeout(() => setSubmitStatus(''), 2000);
    }
  };

  return (
    <div className="news-shell">
      <header className="news-hero">
        <div className="news-hero-grid">
          <div className="news-hero-content">
            <span className="news-kicker">Live Desk</span>
            <h1>BlinkURL Newsroom</h1>
            <p>Live updates across world, business, technology, football, and finance headlines.</p>
            <div className="news-hero-actions">
              <button type="button" onClick={refreshNews} disabled={isRefreshing}>
                {isRefreshing ? 'Refreshing...' : 'Refresh Feed'}
              </button>
              {lastUpdatedAt && (
                <small className="news-updated-at">
                  Last updated: {new Date(lastUpdatedAt).toLocaleString()}
                </small>
              )}
            </div>
          </div>
          <div className="news-hero-panel">
            <div className="hero-stat">
              <span>Stories</span>
              <strong>{summary.totalPosts}</strong>
            </div>
            <div className="hero-stat">
              <span>Sources</span>
              <strong>{summary.sources}</strong>
            </div>
            <div className="hero-stat">
              <span>Categories</span>
              <strong>{summary.categories}</strong>
            </div>
            <div className="hero-stat">
              <span>Latest</span>
              <strong>{summary.latestStory}</strong>
            </div>
          </div>
        </div>
        <div className="news-filter-bar">
          <div className="news-filters">
            <input
              type="search"
              placeholder="Search headlines..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setSubcategory('All');
                setPage(1);
              }}
            >
              {categories.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <select
              value={subcategory}
              onChange={(e) => {
                setSubcategory(e.target.value);
                setPage(1);
              }}
            >
              {subcategories.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
          <div className="news-chips">
            <button
              type="button"
              className={`chip ${category === 'All' ? 'chip-active' : ''}`}
              onClick={() => {
                setCategory('All');
                setSubcategory('All');
                setPage(1);
              }}
            >
              All
            </button>
            {quickCategories.map((value) => (
              <button
                type="button"
                key={value}
                className={`chip ${category === value ? 'chip-active' : ''}`}
                onClick={() => {
                  setCategory(value);
                  setSubcategory('All');
                  setPage(1);
                }}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
        <form className="news-submit" onSubmit={submitStory}>
          <input
            type="url"
            placeholder="Submit a football story link (Sky Sports, etc.)"
            value={submitUrl}
            onChange={(e) => setSubmitUrl(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Optional custom title"
            value={submitTitle}
            onChange={(e) => setSubmitTitle(e.target.value)}
          />
          <button type="submit">Submit Story</button>
          {submitStatus && <span className="news-submit-status">{submitStatus}</span>}
        </form>
      </header>

      {loading && <div className="news-state">Loading news...</div>}
      {error && <div className="news-state">{error}</div>}
      {!loading && !error && filtered.length === 0 && <div className="news-state">No posts found.</div>}

      {!loading && !error && filtered.length > 0 && (
        <>
          {featured && (
            <article className="news-featured">
              <img src={featured.image} alt={featured.title} />
              <div>
                <div className="chip-row">
                  <span className="chip">{featured.category || 'News'}</span>
                  <span className="chip">{featured.subcategory || 'General'}</span>
                </div>
                <h2>{featured.title}</h2>
                <p>{featured.excerpt}</p>
                <div className="source-actions">
                  <button
                    type="button"
                    className="primary"
                    onClick={() => readFullSource(featured)}
                    disabled={!!shortening[featured.slug]}
                  >
                    {shortening[featured.slug] ? 'Building link...' : 'Read Full Source'}
                  </button>
                  <button type="button" onClick={() => copyShortSource(featured)}>
                    {copiedSlug === featured.slug ? 'Copied' : 'Copy Short Link'}
                  </button>
                  <button type="button" onClick={() => markImportant(featured)}>
                    {importantSlug === featured.slug ? 'Saved' : 'Mark Important'}
                  </button>
                  <button type="button" onClick={() => savePost(featured)}>
                    {savedSlug === featured.slug ? 'Saved' : 'Save Post'}
                  </button>
                </div>
                {shareStatus[featured.slug] && <small className="short-link-preview">{shareStatus[featured.slug]}</small>}
                {shortLinks[featured.slug] && <small className="short-link-preview">{shortLinks[featured.slug]}</small>}
                <Link to={`/news/${featured.slug}`}>Open Post</Link>
              </div>
            </article>
          )}

          <section className="news-grid">
            {pageItems.map((post) => (
              <article className="news-card" key={post.id || post.slug}>
                <img src={post.image} alt={post.title} />
                <div className="news-card-body">
                  <div className="chip-row">
                    <span className="chip">{post.category || 'News'}</span>
                    <span className="chip">{post.subcategory || 'General'}</span>
                  </div>
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="news-meta">
                    <small>{new Date(post.publishedAt).toLocaleDateString()}</small>
                    <Link to={`/news/${post.slug}`}>Open</Link>
                  </div>
                  <div className="source-actions card-actions">
                    <button
                      type="button"
                      className="primary"
                      onClick={() => readFullSource(post)}
                      disabled={!!shortening[post.slug]}
                    >
                      {shortening[post.slug] ? 'Building...' : 'Read Full Source'}
                    </button>
                    <button type="button" onClick={() => copyShortSource(post)}>
                      {copiedSlug === post.slug ? 'Copied' : 'Copy Short Link'}
                    </button>
                    <button type="button" onClick={() => markImportant(post)}>
                      {importantSlug === post.slug ? 'Saved' : 'Mark Important'}
                    </button>
                    <button type="button" onClick={() => savePost(post)}>
                      {savedSlug === post.slug ? 'Saved' : 'Save Post'}
                    </button>
                  </div>
                  {shareStatus[post.slug] && <small className="short-link-preview">{shareStatus[post.slug]}</small>}
                  {shortLinks[post.slug] && <small className="short-link-preview">{shortLinks[post.slug]}</small>}
                </div>
              </article>
            ))}
          </section>

          <div className="news-pagination">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
              Previous
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NewsPage;
